import { Row, Col, Form } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import { useSelector } from 'react-redux';

// Custom Component
import ErrorText from '../TextField/ErrorText';
import DatePickerWithYearAndMonth from '../DatePickerWithYearAndMonth/DatePickerWithYearAndMonth';

// Utils
import { countryList } from '../../utils/helper';

// Redux-Functions
import { selectUser } from '../../redux/userSlice';

const PlayerForm = ({ playerIndex, allWatchedValues, control, register, setValue, errors, tournament, tShirtList }) => {
    const user = useSelector(selectUser);

    const reactSelectCustomStyle = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: state.isFocused && "1px solid #FA5000",
            boxShadow: state.isFocused && "0px 0px 2px #FA5000",
            "&:hover": {
                border: "1px solid #FA5000",
                boxShadow: "0px 0px 2px #FA5000"
            }
        }),
        option: (styles, { isSelected }) => {
            return {
                ...styles,
                backgroundColor: isSelected ? '#FA5000' : 'white',
                "&:hover": {
                    backgroundColor: "#FA5000",
                    color: 'white'
                }
            }
        },
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#CCCCCC'
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#CCCCCC'
        })
    }

    /**
        * @function getFieldName
        * @params field
        * @description used to get field name
    */
    const getFieldName = (field) => {
        if (playerIndex !== undefined) {
            return `player_details[${playerIndex}].${field}`;
        } else {
            return field;
        }
    }

    /**
        * @function getError
        * @params field
        * @description used to get error for specific field
    */
    const getError = (field) => {
        if (playerIndex !== undefined && errors?.player_details?.length > 0) {
            return errors?.player_details?.[playerIndex]?.[field];
        } else {
            return errors?.[field];
        }
    }

    return <>
        <Row>
            <Col xs={12} md={6}>
                <Form.Group className="my-2">
                    <Form.Label className='fs-14 fw-500'>Player Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter Player Name"
                        {...register(getFieldName('player_name'), {
                            required: "Player name is required"
                        })}
                    />
                    {getError("player_name") && <ErrorText>{getError("player_name").message}</ErrorText>}
                </Form.Group>
            </Col>
            <Col xs={12} md={6}>
                <Form.Group className="my-2">
                    <Form.Label className='fs-14 fw-500'>Club Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter Club Name"
                        {...register(getFieldName('club_name'))}
                    />
                </Form.Group>
            </Col>
        </Row>
        {tournament && !tournament?.organizer?.is_tshirt_disabled &&
            <Row>
                <Col xs={12} md={6}>
                    <Form.Group className="my-2">
                        <Form.Label className='fs-14 fw-500'>T-Shirt</Form.Label>
                        <Controller
                            name={getFieldName('tshirt_id')}
                            control={control}
                            render={({ field }) => (
                                <Select
                                    placeholder="Select T-shirt"
                                    styles={reactSelectCustomStyle}
                                    options={tShirtList?.map((tShirt) => {
                                        return {
                                            value: tShirt?.id,
                                            label: tShirt?.tshirt_name
                                        }
                                    })}
                                    isSearchable={true}
                                    isClearable={true}
                                    {...field}
                                    onChange={(selectedOption) => {
                                        field.onChange(selectedOption)
                                        setValue(`player_details[${playerIndex}].tshirt_size_id`, null)
                                    }}
                                />
                            )}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                    <Form.Group className="my-2">
                        <Form.Label className='fs-14 fw-500'>T-Shirt Size</Form.Label>
                        <Controller
                            name={getFieldName('tshirt_size_id')}
                            control={control}
                            render={({ field }) => (
                                <Select
                                    placeholder="Select here"
                                    styles={reactSelectCustomStyle}
                                    options={tShirtList?.find(tShirt => tShirt?.id === allWatchedValues?.player_details[playerIndex].tshirt_id?.value)?.tshirt_details?.map((tShirt) => {
                                        return {
                                            value: tShirt?.id,
                                            label: tShirt?.size,
                                            isDisabled: tShirt?.is_out_of_stock || tShirt?.quantity === 0
                                        }
                                    })}
                                    isSearchable={true}
                                    isClearable={true}
                                    {...field}
                                />
                            )}
                            rules={{
                                required: allWatchedValues?.player_details[playerIndex].tshirt_id?.value ? 'T-Shirt Size is required' : false
                            }}
                        />
                        {getError("tshirt_size_id") && <ErrorText>{getError("tshirt_size_id").message}</ErrorText>}
                    </Form.Group>
                </Col>
            </Row>
        }
        <Row>
            <Col xs={12} md={6}>
                <Form.Group className="my-2">
                    <Form.Label className='fs-14 fw-500'>Player Email</Form.Label>
                    <Form.Control
                        type="email"
                        placeholder="Enter email"
                        {...register(getFieldName('player_email'), {
                            required: "Player Email is required",
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: "Invalid email address"
                            }
                        })}
                    />
                    {getError("player_email") && <ErrorText>{getError("player_email").message}</ErrorText>}
                </Form.Group>
            </Col>
            <Col xs={12} md={6}>
                <Form.Group className="my-2">
                    <Form.Label className='fs-14 fw-500'>Player Contact Number</Form.Label>
                    <Form.Control
                        type="tel"
                        placeholder="Enter Player Contact Number"
                        {...register(getFieldName('player_contact'), {
                            required: "Player Contact Number is required",
                            minLength: {
                                value: 9,
                                message: "Contact Number should be 9 digit minimum"
                            },
                            maxLength: {
                                value: 11,
                                message: "Contact Number should be 11 digit maximum"
                            }
                        })}
                    />
                    {getError("player_contact") && <ErrorText>{getError("player_contact").message}</ErrorText>}
                </Form.Group>
            </Col>
        </Row>
        <Row>
            <Col xs={12} md={6}>
                <Form.Group className="my-2">
                    <Form.Label className='fs-14 fw-500'>Player Gender</Form.Label>
                    <Form.Select
                        {...register(getFieldName('player_gender'), {
                            required: "Player Gender is required"
                        })}
                    >
                        <option value='MALE'>Male</option>
                        <option value='FEMALE'>Female</option>
                    </Form.Select>
                    {getError("player_gender") && <ErrorText>{getError("player_gender").message}</ErrorText>}
                </Form.Group>
            </Col>
            <Col xs={12} md={6}>
                <Form.Group className="d-flex flex-column align-items-start my-2">
                    <Form.Label className='text-fs-14 fw-500'>Date of Birth</Form.Label>
                    <Controller
                        control={control}
                        name={getFieldName('player_dob')}
                        render={({ field }) => (
                            <DatePickerWithYearAndMonth
                                {...field}
                                selected={field.value}
                                onChange={(date) => field.onChange(date)}
                                maxDate={new Date()}
                            />
                        )}
                        rules={{ required: 'Date of Birth is required' }}
                    />
                    {getError("player_dob") && <ErrorText className="mt-0">{getError("player_dob").message}</ErrorText>}
                </Form.Group>
            </Col>
        </Row>
        <Row>
            <Col xs={12} md={tournament?.sports_type === "PICKLEBALL" ? 4 : 6}>
                <Form.Group className="my-2">
                    <Form.Label className='fs-14 fw-500'>Player IC/Passport Number</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter Player IC/Passport Number"
                        {...register(getFieldName('player_idcard'), {
                            required: "Player IC/Passport Number is required"
                        })}
                    />
                    {getError("player_idcard") && <ErrorText>{getError("player_idcard").message}</ErrorText>}
                </Form.Group>
            </Col>
            <Col xs={12} md={tournament?.sports_type === "PICKLEBALL" ? 4 : 6}>
                <Form.Group className="my-2">
                    <Form.Label className='fs-14 fw-500'>Country</Form.Label>
                    <Form.Select
                        {...register(getFieldName('nationality'), {
                            required: "Player Country is required"
                        })}
                    >
                        <option value="">Select player country</option>
                        {countryList?.map((item, index) => {
                            return <option value={item} key={index}>{item}</option>
                        })}
                    </Form.Select>
                    {getError("nationality") && <ErrorText>{getError("nationality").message}</ErrorText>}
                </Form.Group>
            </Col>
            {tournament?.sports_type === "PICKLEBALL" &&
                <Col xs={12} md={4}>
                    <Form.Group className="my-2">
                        <Form.Label className='fs-14 fw-500'>DUPR Rating</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter here"
                            {...register(getFieldName('dupr_rating'))}
                        />
                    </Form.Group>
                </Col>
            }
        </Row>

        <Form.Check
            className='mb-2'
            type='checkbox'
            label={<span role='button' className='fs-14 fw-600 text-secondary'>
                {user?.type === 'organizer' ?
                    "Are you a State/District/National Player?"
                    :
                    "Yes, I am a State/District/National Player"
                }
            </span>}
            id={`${playerIndex ? `proffesional_player${playerIndex}` : `proffesional_player`}`}
            {...register(getFieldName('proffesional_player'))}
        />

        <Form.Check
            type='checkbox'
            label={<span role='button' className='fs-14 fw-600 text-secondary'>
                {user?.type === 'organizer' ?
                    "Declare that the information shared are true and confirm"
                    :
                    "I agree that the information shared are true and confirms"
                }
            </span>}
            id={`${playerIndex ? `declare${playerIndex}` : `declare`}`}
            required
        />
    </>
};

export default PlayerForm;